package fr.frux.feature.auth

import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.shared.identity.IdentityPolicy
import fr.frux.shared.identity.IdentityProblem

/**
 * Échecs présentables du parcours passwordless (ADR 0012), typés au lieu d'une chaîne opaque
 * (#90). Le libellé français vit en composition dans `AuthErrorMessages.kt` (#176).
 *
 * Chaque cas dérive d'une source déjà typée ailleurs dans le domaine — [IdentityProblem]
 * pour les refus de saisie locaux, [fr.frux.shared.domain.repository.VerifyOutcome] pour les
 * refus métier du serveur, [AppError] pour les échecs de transport — jamais d'une liste inventée
 * côté écran.
 */
sealed class AuthError {
    /** Email ou pseudo refusé par [IdentityPolicy] — saisie locale, ou renvoyé par le serveur
     * pour le pseudo via `VerifyOutcome.UsernameInvalid` (même règle, deux points d'application). */
    data class Identity(
        val problem: IdentityProblem,
    ) : AuthError()

    /** Champ code vide — pas encore de format à évaluer. */
    data object CodeRequired : AuthError()

    /** Code d'une longueur ou d'un format différent de [IdentityPolicy.OTP_CODE_LENGTH] chiffres. */
    data object CodeInvalidFormat : AuthError()

    /** `VerifyOutcome.InvalidCode` : code refusé par le serveur (faux ou expiré). */
    data object CodeInvalidOrExpired : AuthError()

    /** `VerifyOutcome.UsernameTaken` : pseudo déjà pris par un autre compte. */
    data object UsernameTaken : AuthError()

    /** Échec de transport (réseau, 5xx, quota, imprévu...) remonté par le repository. */
    data class Remote(
        val error: AppError,
    ) : AuthError()
}

/** Convertit un échec réseau brut en [AuthError.Remote] sans jamais laisser fuir le détail technique. */
fun Throwable.toAuthError(): AuthError.Remote = AuthError.Remote(asAppError())
